// src/components/dashboard/CategoriesView.tsx
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '@/services/api';
import { CategoryGrid } from './CategoryGrid';
import { useRealtimeCategoryInsights } from '@/hooks/useRealtimeCategoryInsights';

interface CategoriesViewProps {
  onCategoryClick: (categoryName: string) => void;
  userId: string;
  isDemo?: boolean;
}

export const CategoriesView: React.FC<CategoriesViewProps> = ({
  onCategoryClick,
  userId,
  isDemo = false
}) => {
  // Fetch category insights with React Query
  const { data: categoryData, isLoading, error, refetch } = useQuery({
    queryKey: ['categoryInsights'],
    queryFn: async () => {
      console.log('🔄 [CategoriesView] Fetching category insights...');
      const response = await apiService.getCategoryInsights();
      console.log('📥 [CategoriesView] Category insights response:', {
        success: response.success,
        hasData: !!response.data,
        insightsCount: response.data?.insights?.length || 0,
        timestamp: new Date().toISOString()
      });
      if (response.success && response.data) {
        return response.data.insights || [];
      }
      throw new Error('Failed to fetch category insights');
    },
    staleTime: isDemo ? Infinity : 0,
    gcTime: isDemo ? Infinity : 0,
    refetchOnMount: !isDemo,
    refetchOnWindowFocus: false,
    enabled: !isDemo, // Disable query in demo mode
  });

  // Real-time category insights
  const {
    categories: realtimeCategories,
    processingCategories,
    setCategories: setRealtimeCategories,
  } = useRealtimeCategoryInsights({
    userId,
    initialData: categoryData || [],
  });

  // Use real-time data if available, otherwise fall back to API data
  // Show ALL categories in this view (Imaging, Stool/Urine, and standard Lab tests)
  const categoriesRaw = realtimeCategories.length > 0 ? realtimeCategories : (categoryData || []);
  const categories = Array.isArray(categoriesRaw) ? categoriesRaw : [];

  // Show error state
  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        fontFamily: 'var(--font-primary)',
        gap: 'var(--space-4)'
      }}>
        <p style={{ color: 'var(--color-error)' }}>
          Error: {error instanceof Error ? error.message : 'Failed to load categories'}
        </p>
        <button
          onClick={() => refetch()}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            backgroundColor: 'var(--color-primary)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius)',
            cursor: 'pointer',
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // Show loading state
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        fontFamily: 'var(--font-primary)',
        gap: 'var(--space-4)'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid var(--color-border)',
          borderTopColor: 'var(--color-primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: 'var(--color-muted)' }}>Loading categories...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: 'var(--font-primary)',
      maxWidth: '1400px',
      margin: '0 auto',
      padding: 'var(--space-6)'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: 'var(--space-8)'
      }}>
        <h1 style={{
          fontFamily: 'var(--font-primary)',
          fontSize: 'var(--text-3xl)',
          fontWeight: 'var(--font-bold)',
          color: 'var(--color-primary)',
          marginBottom: 'var(--space-2)',
          letterSpacing: '0.5px'
        }}>
          HEALTH CATEGORIES
        </h1>
        <p style={{
          fontFamily: 'var(--font-primary)',
          fontSize: 'var(--text-base)',
          color: 'var(--color-muted)',
          margin: 0
        }}>
          Select a category to view detailed insights and metrics
        </p>
      </div>

      {/* Category Grid */}
      <CategoryGrid
        categories={categories}
        onCategoryClick={onCategoryClick}
        processingCategories={processingCategories}
      />

      {/* Empty State */}
      {categories.length === 0 && !isLoading && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '300px',
          backgroundColor: 'var(--color-surface)',
          borderRadius: 'var(--radius)',
          border: '1px solid var(--color-border)',
          padding: 'var(--space-8)'
        }}>
          <p style={{
            fontFamily: 'var(--font-primary)',
            fontSize: 'var(--text-lg)',
            color: 'var(--color-muted)',
            textAlign: 'center'
          }}>
            No health categories available yet.
            <br />
            Upload a lab report to get started.
          </p>
        </div>
      )}
    </div>
  );
};
